import { Canvas } from 'fabric/fabric-impl';
import { useCallback, useContext, useEffect, useState } from 'react';

import { autoZoomPanToFit, updateShapeByRotation, updateShapeByTranslation, zoomIn, zoomOut } from '@canvas-common';
import { CANVAS_OBJECT_TYPE_PREFIX, FAUX_SHAPE_INDEX } from '@common/constants';
import { ShapeType, TOOLBAR_BUTTON } from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { DrawingAppliance, DrawingShape } from '@common/models';
import { DrawingShapeContext, usePricedProductsContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetCookingAppliances } from '@redux/slices/cookingApplianceSlice';
import { resetSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { resetSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import {
    resetActiveDrawingAppliance,
    selectActiveDrawingAppliance,
    selectDrawingShapesUndoStackIndex,
    setNeedRedrawCanvas,
    setPreventRepricing,
    setSkipUndoStackUpdates,
} from '@redux/slices/drawingShapeSlice';
import { resetSelectedEdge } from '@redux/slices/edgeSlice';
import { selectFabricCanvas } from '@redux/slices/fabricSlice';
import { resetSelectedDrawingTab, updateSelectedDrawingTab } from '@redux/slices/shapeSlice';
import { resetSelectedSink, resetSinkSelectedOption, selectSinkSelectedOption } from '@redux/slices/sinkSlice';
import { resetSelectedToolbarButton, selectSelectedToolbarButton } from '@redux/slices/toolbarCanvasSlice';

import useAppliance from '../../appliances/useAppliance';

const isActiveDrawingAppliance =
    (activeDrawingAppliance: string) =>
    (sink: DrawingAppliance): boolean =>
        `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${sink.id}` === activeDrawingAppliance;
const useToolBar = () => {
    const {
        areThereShapesInTheDrawingArea,
        drawingShapes,
        updateDrawingShapes,
        activeDrawingShape,
        lastActiveDrawingShape,
        updateActiveDrawingShape,
        pullDrawingShapesUndoStack,
        clearDrawingShapesUndoStack,
        autoPlaceShapeTranslation,
        updateActiveDrawingShapeId,
    } = useContext(DrawingShapeContext);

    const activeDrawingAppliance = useAppSelector(selectActiveDrawingAppliance);
    const drawingShapesUndoStackIndex = useAppSelector(selectDrawingShapesUndoStackIndex);

    const selectedToolbarButton = useAppSelector(selectSelectedToolbarButton);

    const canvas = useAppSelector(selectFabricCanvas);
    const dispatch = useAppDispatch();

    usePricedProductsContext(); //TODO Do we still need that useffect from here

    const sinkSelectedOption = useAppSelector(selectSinkSelectedOption);
    const [isToolbarBtnEnabled, setIsToolbarBtnEnabled] = useState<boolean>(true);
    const { rotateActiveDrawingAppliance } = useAppliance();

    const duplicateActiveDrawingShape = () => {
        if (!areThereShapesInTheDrawingArea || !activeDrawingShape) {
            return;
        }

        const sourceShape = drawingShapes.find((drawingShape) => drawingShape.id === activeDrawingShape.id);
        if (!sourceShape) {
            return;
        }

        // create a new shape that is a duplicate of the current active shape
        // The next line change fixes an issue with duplicate shapes being pulled from the wrong stack item
        // after an undo operation
        const duplicateShape: DrawingShape = DrawingShape.cloneDrawingShape(sourceShape, true, false);
        const newPoly = duplicateShape.highlightPoints || [];
        const destVector = autoPlaceShapeTranslation(newPoly);
        updateShapeByTranslation(destVector, duplicateShape);
        updateActiveDrawingShape(duplicateShape);
        drawingShapes.splice(drawingShapes.length, 0, duplicateShape);

        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
        setIsToolbarBtnEnabled(false);
    };

    const removeActiveDrawingAppliance = useCallback(() => {
        if (!activeDrawingAppliance) {
            return;
        }

        /**
         *  If there is a shape that is not the FAUX shape then the tab should have the shape selected.
         *  There is no need to call updateActiveDrawingShape. The activeDrawingShape will default on its own.
         */
        if (drawingShapes.some((shape) => shape.type !== ShapeType.FAUX_SHAPE)) {
            dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
        }

        updateDrawingShapes((drawingShapes) => {
            drawingShapes.forEach((shape: DrawingShape) => {
                const foundIndex = shape.appliances.findIndex(isActiveDrawingAppliance(activeDrawingAppliance));

                if (foundIndex > -1) {
                    // remove shape from drawingShapes array and update the drawing shape context and redraw
                    dispatch(resetActiveDrawingAppliance());
                    dispatch(resetSelectedSink());
                    shape.appliances.splice(foundIndex, 1);
                    if (shape.type !== ShapeType.FAUX_SHAPE) {
                        updateActiveDrawingShapeId(shape.id);
                    }

                    dispatch(setNeedRedrawCanvas(true));
                    dispatch(resetSelectedToolbarButton());
                    setIsToolbarBtnEnabled(false);
                }
            });
            return [...drawingShapes];
        });
    }, [activeDrawingAppliance, dispatch, updateActiveDrawingShapeId, updateDrawingShapes]);
    const rotateActiveDrawingShape = (clockwise: boolean) => {
        if (!areThereShapesInTheDrawingArea || !activeDrawingShape) {
            return;
        }
        // find selected shape
        const foundIndex = drawingShapes.findIndex(
            (drawingShape: DrawingShape) => drawingShape.id === activeDrawingShape?.id
        );
        if (foundIndex > -1) {
            // rotate selected shape
            dispatch(setPreventRepricing(true));
            updateShapeByRotation(canvas, drawingShapes[foundIndex], clockwise);
            updateDrawingShapes([...drawingShapes]);
            dispatch(setNeedRedrawCanvas(true));

            // reset buttons and drawing state
            dispatch(resetSelectedToolbarButton());
            setIsToolbarBtnEnabled(false);
        }
    };

    const resetActiveDrawingShape = (dShapes: DrawingShape[]) => {
        //  fix for EE-1664
        if (lastActiveDrawingShape) {
            const foundIndex = dShapes.findIndex(
                (drawingShape: DrawingShape) => drawingShape.id === activeDrawingShape?.id
            );
            if (foundIndex > -1) {
                updateActiveDrawingShape(dShapes[foundIndex]);
            } else if (dShapes.length > 1) {
                updateActiveDrawingShape(dShapes[1]);
            }
        }
    };

    const redoOneAction = () => {
        dispatch(setSkipUndoStackUpdates(true));
        dispatch(resetSelectedToolbarButton());

        const pulledDrawingShapes = pullDrawingShapesUndoStack(false);

        if (pulledDrawingShapes) {
            updateDrawingShapes(pulledDrawingShapes);
            resetActiveDrawingShape(pulledDrawingShapes);
        }
    };

    const configAfterRemovedLastShape = useCallback(() => {
        const resetSelectedCardsOptions = () => {
            dispatch(resetSelectedEdge());
            dispatch(resetSelectedCornerRadius());
            dispatch(resetSelectedDiagonalCorner());
            // sink selection
            if (sinkSelectedOption) {
                dispatch(resetSinkSelectedOption());
            }
            // TO DO: reset other future selections on cards when removing the last shape
        };
        const drawingShapesResetAppliances = [...drawingShapes];
        drawingShapesResetAppliances[FAUX_SHAPE_INDEX].appliances = [] satisfies DrawingAppliance[];
        clearDrawingShapesUndoStack();
        const drawingShapesAfterRemoval = drawingShapesResetAppliances.toSpliced(1, drawingShapes.length - 1);
        updateDrawingShapes([...drawingShapesAfterRemoval]);
        dispatch(setSkipUndoStackUpdates(true));
        // updateSelectedEdgeByDefault(undefined); // TODO: investigate this function dos not accept undefined
        dispatch(setNeedRedrawCanvas(true));
        // when all shapes are removed, the predefined shapes menu will be selected and all the data from drawing area will be reset
        updateActiveDrawingShape(undefined);
        dispatch(resetSelectedDrawingTab());
        // remove previously selected options from the cards below design countertop
        resetSelectedCardsOptions();
    }, [clearDrawingShapesUndoStack, dispatch, drawingShapes, updateActiveDrawingShape, updateDrawingShapes]);

    const rotateDrawingShapeOrAppliance = (clockwise: boolean, canvas: Canvas) => {
        if (activeDrawingShape) {
            rotateActiveDrawingShape(clockwise);
        } else {
            rotateActiveDrawingAppliance(canvas, clockwise);
        }
    };

    const removeShape = useCallback(() => {
        if (activeDrawingShape) {
            if (!areThereShapesInTheDrawingArea) {
                return;
            }

            const foundIndex = drawingShapes.findIndex(
                (drawingShape: DrawingShape) => drawingShape.id === activeDrawingShape?.id
            );
            if (foundIndex > -1) {
                // remove shape from drawingShapes array and update the drawing shape context and redraw
                dispatch(resetSelectedSink());
                dispatch(resetCookingAppliances());
                const drawingShapesWithActiveRemoved = drawingShapes.toSpliced(foundIndex, 1);
                updateDrawingShapes([...drawingShapesWithActiveRemoved]);
                dispatch(setNeedRedrawCanvas(true));

                // after removing shape  free drawing tab will be selected by default
                dispatch(resetSelectedToolbarButton());
                dispatch(resetSelectedDrawingTab());

                setIsToolbarBtnEnabled(false);
                dispatch(resetSelectedDiagonalCorner());
                // if we have any real shapes remaining, make a new active shape
                if (drawingShapesWithActiveRemoved.filter((shape) => shape.id !== ShapeType.FAUX_SHAPE).length) {
                    updateActiveDrawingShape(drawingShapesWithActiveRemoved[1]);
                } else {
                    // clean up after the drawing is empty
                    configAfterRemovedLastShape();
                }
            }
        } else if (activeDrawingAppliance) {
            removeActiveDrawingAppliance();
        }
    }, [
        activeDrawingAppliance,
        activeDrawingShape,
        areThereShapesInTheDrawingArea,
        configAfterRemovedLastShape,
        dispatch,
        drawingShapes,
        removeActiveDrawingAppliance,
        updateActiveDrawingShape,
        updateDrawingShapes,
    ]);

    // duplicate button event
    useEffect(() => {
        if (selectedToolbarButton === TOOLBAR_BUTTON.DUPLICATE && activeDrawingShape) {
            duplicateActiveDrawingShape();
            dispatch(resetSelectedToolbarButton());
        }
    }, [selectedToolbarButton, dispatch]);

    // rotate buttons events
    useEffect(() => {
        if (selectedToolbarButton === TOOLBAR_BUTTON.ROTATE_CLOCKWISE) {
            rotateDrawingShapeOrAppliance(true, canvas);
        }

        if (selectedToolbarButton === TOOLBAR_BUTTON.ROTATE_COUNTERCLOCKWISE) {
            rotateDrawingShapeOrAppliance(false, canvas);
        }
    }, [selectedToolbarButton]);

    const undoOneAction = () => {
        dispatch(setSkipUndoStackUpdates(true));
        dispatch(resetSelectedToolbarButton());

        if (drawingShapesUndoStackIndex === 1) {
            configAfterRemovedLastShape();
        } else {
            const pulledDrawingShapes = pullDrawingShapesUndoStack(true);
            if (pulledDrawingShapes) {
                updateDrawingShapes(pulledDrawingShapes);
                if (pulledDrawingShapes.length === 1) {
                    configAfterRemovedLastShape();
                }
                resetActiveDrawingShape(pulledDrawingShapes);
            }
        }
        dispatch(setNeedRedrawCanvas(true));
        canvas.renderAll();
    };

    // undo-redo buttons events
    useEffect(() => {
        if (selectedToolbarButton === TOOLBAR_BUTTON.REDO) {
            redoOneAction();
            dispatch(setNeedRedrawCanvas(true));
            canvas.renderAll();
        }
    }, [selectedToolbarButton]);

    // zoom buttons events
    useEffect(() => {
        if (selectedToolbarButton === TOOLBAR_BUTTON.ZOOM_IN) {
            zoomIn(canvas);
        }

        if (selectedToolbarButton === TOOLBAR_BUTTON.ZOOM_OUT) {
            zoomOut(canvas);
        }

        if (selectedToolbarButton === TOOLBAR_BUTTON.ZOOM_TO_FIT) {
            autoZoomPanToFit(canvas, true, true);
        }

        dispatch(resetSelectedToolbarButton());
        canvas.renderAll();
    }, [selectedToolbarButton, dispatch]);

    // enable toolbar buttons  when activeShape is in drawingShapes array
    useEffect(() => {
        const currentActiveShape = drawingShapes.findIndex(
            (drawingShape: DrawingShape) => drawingShape?.id === activeDrawingShape?.id
        );
        if (currentActiveShape > -1 && !isToolbarBtnEnabled) {
            setIsToolbarBtnEnabled(true);
        }
    }, [isToolbarBtnEnabled, activeDrawingShape]);

    return {
        isToolbarBtnEnabled,
        undoOneAction,
        removeShape,
    };
};

export default useToolBar;
